<!-- Modern Video Player Component - Bootstrap 5.3.7 -->
<div class="video-player-container">
  
  <!-- Navigation Breadcrumb -->
  <div class="container-fluid py-2 bg-light border-bottom">
    <div class="container">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb mb-0">
          <li class="breadcrumb-item">
            <a [routerLink]="['/app/classes']" class="text-decoration-none">
              <i class="fa fa-arrow-left me-1"></i>
              {{ 'VIDEO.BACK_TO_CLASSES' | translate }}
            </a>
          </li>
          <li class="breadcrumb-item active" aria-current="page">
            {{ 'VIDEO.CLASS_TITLE_PREFIX' | translate }} {{this.class?.order}}
          </li>
        </ol>
      </nav>
    </div>
  </div>

  <!-- Class Header -->
  <div class="class-header bg-primary text-white">
    <div class="container py-4">
      <div class="row align-items-center">
        <div class="col-12 col-lg-8">
          <div class="class-header__content">
            <h1 class="class-header__title mb-2">
              <span class="badge bg-warning text-dark me-3">
                {{ 'VIDEO.CLASS_TITLE_PREFIX' | translate }} {{this.class?.order}}
              </span>
              {{this.class?.smallTitle}}
            </h1>
            <p class="class-header__description mb-0 lead">
              {{this.class?.description}}
            </p>
          </div>
        </div>
        <div class="col-12 col-lg-4 text-lg-end mt-3 mt-lg-0">
          <div class="class-header__progress">
            <div class="card bg-light text-dark">
              <div class="card-body text-center py-3">
                <h6 class="card-title mb-1">
                  <i class="fa fa-play-circle me-1"></i>
                  {{ 'VIDEO.PROGRESS' | translate }}
                </h6>
                <div class="progress mb-2" style="height: 8px;">
                  <div class="progress-bar bg-warning" 
                       role="progressbar" 
                       [style.width.%]="getVideoProgress()"
                       [attr.aria-valuenow]="getVideoProgress()"
                       aria-valuemin="0" 
                       aria-valuemax="100">
                  </div>
                </div>
                <small class="text-muted">{{getVideoProgress()}}% {{ 'VIDEO.COMPLETE' | translate }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Video Player Section -->
  <div class="video-section">
    <div class="container py-4">
      <div class="row justify-content-center">
        <div class="col-12 col-xl-10">
          
          <!-- Video Player Card -->
          <div class="card shadow-sm video-player-card">
            <div class="card-body p-0">
              
              <!-- Video Container with Aspect Ratio -->
              <div class="video-container">
                <div class="ratio ratio-16x9">
                  <video 
                    #videoClass 
                    id="videoClass" 
                    (timeupdate)="tickTimeVideo($event)"
                    (loadedmetadata)="onVideoLoaded($event)"
                    (error)="onVideoError($event)"
                    class="video-player"
                    controls 
                    controlsList="nodownload"
                    [attr.aria-label]="'Video for ' + this.class?.smallTitle"
                    preload="metadata">
                    <source [src]="videoUrl" type="video/mp4">
                    <p class="text-center p-4 text-muted">
                      <i class="fa fa-exclamation-triangle me-2"></i>
                      {{ 'VIDEO.VIDEO_NOT_SUPPORTED' | translate }}
                    </p>
                  </video>
                </div>
                
                <!-- Video Loading Overlay -->
                <div *ngIf="isVideoLoading" class="video-loading-overlay">
                  <div class="text-center">
                    <div class="spinner-border text-primary mb-3" role="status">
                      <span class="visually-hidden">{{ 'VIDEO.LOADING_VIDEO' | translate }}...</span>
                    </div>
                    <p class="text-muted">{{ 'VIDEO.LOADING_VIDEO_CONTENT' | translate }}...</p>
                  </div>
                </div>
              </div>
              
            </div>
          </div>

          <!-- Video Information -->
          <div class="card mt-4 video-info-card">
            <div class="card-header bg-light">
              <h5 class="card-title mb-0">
                <i class="fa fa-info-circle me-2 text-primary"></i>
                {{ 'VIDEO.CLASS_INFORMATION' | translate }}
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="text-muted mb-2">{{ 'VIDEO.CLASS_DETAILS' | translate }}</h6>
                  <ul class="list-unstyled">
                    <li class="mb-2">
                      <i class="fa fa-bookmark me-2 text-warning"></i>
                      <strong>{{ 'VIDEO.CLASS' | translate }}:</strong> {{this.class?.order}} - {{this.class?.smallTitle}}
                    </li>
                    <li class="mb-2">
                      <i class="fa fa-clock me-2 text-info"></i>
                      <strong>{{ 'VIDEO.DURATION' | translate }}: </strong> <span id="videoDuration">--:--</span>
                    </li>
                    <li class="mb-2">
                      <i class="fa fa-eye me-2 text-success"></i>
                      <strong>{{ 'VIDEO.PROGRESS' | translate }}: </strong> <span id="videoProgress">--:--</span>
                    </li>
                  </ul>
                </div>
                <div class="col-md-6">
                  <h6 class="text-muted mb-2">{{ 'VIDEO.NEXT_STEPS' | translate }}</h6>
                  
                  <!-- Dynamic status messages -->
                  <div class="alert mb-3" 
                       [ngClass]="{
                         'alert-warning': !isVideoCompleted,
                         'alert-info': isVideoCompleted && !isHandoutCompleted,
                         'alert-success': isVideoCompleted && isHandoutCompleted
                       }">
                    <div class="d-flex align-items-center">
                      <i class="me-2" 
                         [ngClass]="{
                           'fa fa-play-circle': !isVideoCompleted,
                           'fa fa-download': isVideoCompleted && !isHandoutCompleted,
                           'fa fa-check-circle': isVideoCompleted && isHandoutCompleted
                         }"></i>
                      <div>
                        <strong *ngIf="!isVideoCompleted">
                          {{ 'VIDEO.COMPLETE_VIDEO_MESSAGE' | translate }}
                        </strong>
                        <strong *ngIf="isVideoCompleted && !isHandoutCompleted">
                          {{ 'VIDEO.DOWNLOAD_HANDOUT_MESSAGE' | translate }}
                        </strong>
                        <strong *ngIf="isVideoCompleted && isHandoutCompleted">
                          {{ 'VIDEO.READY_FOR_QUIZ_MESSAGE' | translate }}
                        </strong>
                      </div>
                    </div>
                    <small class="d-block mt-1 text-muted" *ngIf="!isVideoCompleted">
                      {{ 'VIDEO.WATCH_COMPLETE_INSTRUCTION' | translate }}
                    </small>
                  </div>
                  
                  <div class="d-grid gap-2">
                    <button class="btn btn-outline-primary btn-sm" 
                            [disabled]="!isVideoCompleted"
                            (click)="navigateToHandout()">
                      <i class="fa fa-file-pdf me-2"></i>
                      {{ 'VIDEO.DOWNLOAD_HANDOUT' | translate }}
                    </button>
                    <button class="btn btn-outline-success btn-sm" 
                            [disabled]="!isHandoutCompleted"
                            (click)="navigateToQuiz()">
                      <i class="fa fa-question-circle me-2"></i>
                      {{ 'VIDEO.TAKE_QUIZ' | translate }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  </div>
  
</div>
  